import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import globalAxios from "axios";
import {
	FiAlertTriangle,
	FiCheckCircle,
	FiChevronDown,
	FiClock,
	FiColumns,
	FiEdit3,
	FiExternalLink,
	FiFileText,
	FiFilter,
	FiRefreshCw,
	FiSettings,
	FiTrash2,
	FiUploadCloud
} from "react-icons/fi";
import { usePageHeader } from "../../hooks/usePageHeader";
import { useRealtimeEvent } from "../../Realtime/RealtimeProvider";
import { Modal } from "../../Components/Modal";
import { LoadingSpinner } from "../../Components/LoadingSpinner";
import GatedButton from "../../Components/GatedButton";
import ConnectionsModalButton from "../../Components/ConnectionsModalButton";
import { UserAvatar } from "../../Components/UserAvatar";
import { useUserDirectory } from "../../Components/Account/useUserDirectory";
import { usePermissions } from "../../Auth/PermissionsContext";
import { PREVIEW_SITE_URL, buildPublicUrl } from "../../Helper/siteLinks";
import PreviewPill from "./PreviewPill";
import IntegrityAnalyzeModal from "./IntegrityAnalyzeModal";
import DeploymentActivity from "../Deployment/DeploymentActivity";
import { AdvancedDeploymentTools } from "./AdvancedPanel";
import {
	DeploymentDiffBody,
	DiffVerdictChip,
	diffVerdict,
	useDeploymentDiff
} from "../../Components/Deployment/deploymentDiff";
import { GROUP_PAGE_SIZE, useProductionOverview } from "./usePublicationData";
import {
	actionChipClass,
	actionLabelFr,
	exactDateFr,
	iconForContentType,
	isDeletionAction,
	lockMessageFrom,
	pluralFr,
	promoteTokenForType,
	relativeTimeFr,
	type ProductionPublishGroup,
	type ProductionPublishItem
} from "./publicationUtils";

/**
 * « Publication » en vue MAÎTRE-DÉTAIL : à gauche la file de production (échecs puis groupes
 * par type, dépliables jusqu'à l'élément), à droite la sélection avec ses DIFFÉRENCES rendues
 * par le socle visuel commun avec /audit (ChangeDiffViewer, pastilles d'action colorées).
 * Sélectionner un GROUPE empile les comparaisons de tous ses éléments ; sélectionner un
 * ÉLÉMENT montre la sienne, avec l'action de publication périmétrée à côté.
 */

/** Admin editor deep-link for a pending item (page vs generic record). */
const editorHrefFor = (item: ProductionPublishItem): string | null => {
	const id = item.contentId;
	if (!id) return null;
	const type = (item.contentType ?? "").trim();
	if (type.toLowerCase() === "page") return `/page/${encodeURIComponent(id)}`;
	if (!type) return null;
	return `/content/${encodeURIComponent(type)}/${encodeURIComponent(id)}`;
};

/**
 * Preview URL for a pending item, from its public path (stamped on the row, or resolved by the
 * overview from the admin source of truth). A page's slug IS its path; a record whose path could
 * not be resolved gets NO link rather than a wrong one (its bare id is not a page).
 */
export const previewHrefFor = (item: ProductionPublishItem): string | null => {
	const isPage = (item.contentType ?? "").trim().toLowerCase() === "page";
	const path = item.pagePaths?.[0] ?? (isPage ? item.slug : null);
	return path ? buildPublicUrl(PREVIEW_SITE_URL, path) : null;
};

type AuthorResolvers = {
	resolveId: (nameOrEmail?: string | null) => string | null;
	resolvePicture: (nameOrEmail?: string | null) => string | null;
};

/** Identifies which publish action is in flight, so each button owns its spinner. */
type PublishKey = "all" | `type:${string}` | `item:${string}` | "retry-all";

type Banner = { tone: "success" | "error"; text: string };

/** Ce que l'utilisateur regarde dans le panneau de droite. */
type Selection =
	| { kind: "group"; typeId: string }
	| { kind: "item"; queueId: string }
	| null;

const PRIMARY_BTN =
	"inline-flex items-center gap-2 rounded-xl bg-[#005CA9] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#004b8a] disabled:cursor-not-allowed disabled:bg-slate-300";

const itemKey = (item: ProductionPublishItem) => item.id || `${item.contentId}-${item.slug}`;

/** Bouton d'action d'un élément : Publier / Retirer de la production / Réessayer, périmétré. */
const ItemPublishButton: React.FC<{
	item: ProductionPublishItem;
	failed: boolean;
	publishing: boolean;
	disabled: boolean;
	onPublish: (item: ProductionPublishItem) => void;
	tokenForType: (contentTypeId?: string | null) => string;
	compact?: boolean;
}> = ({ item, failed, publishing, disabled, onPublish, tokenForType, compact = false }) => {
	if (!item.contentId) return null;
	const isDeletion = isDeletionAction(item.action);
	return (
		<GatedButton
			token={tokenForType(item.contentType)}
			onClick={() => onPublish(item)}
			disabled={disabled}
			title={
				failed
					? "Réessayer la publication"
					: isDeletion
					? "Retirer ce contenu de la production (propager la suppression)"
					: "Publier cet élément en production"
			}
			className={`inline-flex items-center justify-center gap-1 rounded-lg border font-semibold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-40 ${
				compact ? "h-7 px-2 text-[11px]" : "h-9 px-3 text-xs"
			} ${
				failed || isDeletion
					? "border-rose-300 bg-white text-rose-700 hover:bg-rose-50"
					: "border-slate-200 bg-white text-slate-600 hover:border-[#005CA9]/40 hover:text-[#005CA9]"
			}`}
		>
			{publishing ? (
				<LoadingSpinner size="sm" inline />
			) : failed ? (
				<FiRefreshCw className="h-3 w-3" />
			) : isDeletion ? (
				<FiTrash2 className="h-3 w-3" />
			) : (
				<FiUploadCloud className="h-3.5 w-3.5" />
			)}
			{failed ? "Réessayer" : isDeletion ? "Retirer de la production" : "Publier"}
		</GatedButton>
	);
};

/** Ligne d'élément dans la LISTE de gauche : pastille d'action, titre, slug ; cliquable. */
const ItemRow: React.FC<{
	item: ProductionPublishItem;
	selected: boolean;
	failed?: boolean;
	onSelect: () => void;
}> = ({ item, selected, failed = false, onSelect }) => {
	const heading = item.title || item.slug || item.contentId || "?";
	return (
		<button
			type="button"
			onClick={onSelect}
			className={`flex w-full items-center gap-2 rounded-lg border px-2.5 py-2 text-left transition ${
				selected
					? "border-[#005CA9]/50 bg-[#005CA9]/5"
					: failed
					? "border-rose-200 bg-rose-50/40 hover:border-rose-300"
					: "border-transparent hover:border-slate-200 hover:bg-slate-50"
			}`}
		>
			<span className={`inline-flex shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${actionChipClass(item.action)}`}>
				{actionLabelFr(item.action)}
			</span>
			<span className="min-w-0 flex-1">
				<span className="block truncate text-xs font-medium text-slate-700" title={heading}>
					{heading}
					<span className="ml-1 font-normal text-slate-400">({item.language})</span>
				</span>
				{item.slug && item.slug !== heading && (
					<span className="block truncate font-mono text-[10px] text-slate-400" title={item.slug}>{item.slug}</span>
				)}
				{failed && item.error && (
					<span className="block truncate text-[10px] text-rose-600" title={item.error}>{item.error}</span>
				)}
			</span>
		</button>
	);
};

/** En-tête du panneau de détail d'un élément : identité, auteur, liens, action de publication. */
const ItemDetailHeader: React.FC<{
	item: ProductionPublishItem;
	failed: boolean;
	publishing: boolean;
	disabled: boolean;
	onPublish: (item: ProductionPublishItem) => void;
	tokenForType: (contentTypeId?: string | null) => string;
	resolvers: AuthorResolvers;
}> = ({ item, failed, publishing, disabled, onPublish, tokenForType, resolvers }) => {
	const heading = item.title || item.slug || item.contentId || "?";
	const author = item.author ?? null;
	const authorName = author && author.toUpperCase() !== "SYSTEM" ? author : "Système";
	const authorId = author && author.toUpperCase() !== "SYSTEM" ? resolvers.resolveId(author) : null;
	const authorPic = author && author.toUpperCase() !== "SYSTEM" ? resolvers.resolvePicture(author) : null;
	const previewHref = previewHrefFor(item);
	const editorHref = editorHrefFor(item);
	const whenIso = item.updatedAt ?? item.createdAt;

	return (
		<div className="space-y-2 border-b border-slate-100 px-5 py-4">
			<div className="flex flex-wrap items-center gap-2">
				<span className={`inline-flex shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-medium ${actionChipClass(item.action)}`}>
					{actionLabelFr(item.action)}
				</span>
				<h3 className="min-w-0 flex-1 truncate text-base font-bold text-slate-900" title={heading}>
					{heading}
					<span className="ml-1.5 text-xs font-normal text-slate-400">({item.language})</span>
				</h3>
				<ItemPublishButton
					item={item}
					failed={failed}
					publishing={publishing}
					disabled={disabled}
					onPublish={onPublish}
					tokenForType={tokenForType}
				/>
			</div>
			{item.slug && <p className="truncate font-mono text-[11px] text-slate-400" title={item.slug}>{item.slug}</p>}
			{item.message && <p className="text-xs text-slate-500">{item.message}</p>}
			{failed && item.error && (
				<p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs text-rose-700">
					{pluralFr(item.attemptCount, "tentative")} · {item.error}
				</p>
			)}
			<div className="flex flex-wrap items-center gap-3 pt-1">
				<span className="flex items-center gap-1.5">
					<UserAvatar name={authorName} id={authorId ?? authorName} pictureUrl={authorPic} sizeClass="h-5 w-5" textClass="text-[8px]" title={authorName} />
					<span className="text-[11px] font-medium text-slate-600">{authorName}</span>
					<span className="text-[10px] text-slate-400" title={exactDateFr(whenIso)}>{relativeTimeFr(whenIso)}</span>
				</span>
				{previewHref && (
					<a
						href={previewHref}
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex items-center gap-1 rounded-md border border-sky-200 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700 transition hover:bg-sky-50"
					>
						<FiExternalLink className="h-2.5 w-2.5" /> Preview
					</a>
				)}
				{editorHref && (
					<Link
						to={editorHref}
						className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600 transition hover:bg-slate-50"
					>
						<FiEdit3 className="h-2.5 w-2.5" /> Contenu
					</Link>
				)}
			</div>
		</div>
	);
};

/** Détail d'un ÉLÉMENT : en-tête + sa comparaison brouillon/production. */
const ItemDetailPanel: React.FC<{
	item: ProductionPublishItem;
	failed: boolean;
	publishing: boolean;
	disabled: boolean;
	onPublish: (item: ProductionPublishItem) => void;
	tokenForType: (contentTypeId?: string | null) => string;
	resolvers: AuthorResolvers;
}> = ({ item, failed, publishing, disabled, onPublish, tokenForType, resolvers }) => {
	const state = useDeploymentDiff(item.contentId, "production");
	return (
		<div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
			<ItemDetailHeader
				item={item}
				failed={failed}
				publishing={publishing}
				disabled={disabled}
				onPublish={onPublish}
				tokenForType={tokenForType}
				resolvers={resolvers}
			/>
			<div className="p-4">
				<DeploymentDiffBody state={state} target="production" contentId={item.contentId} />
			</div>
			{state.diff?.deployedAt && (
				<p className="border-t border-slate-100 px-5 py-2 text-[11px] text-slate-400">
					Dernier déploiement : {new Date(state.diff.deployedAt).toLocaleString("fr-FR")}
				</p>
			)}
		</div>
	);
};

/** Une ligne du détail de GROUPE : l'élément, sa pastille de verdict et sa comparaison dépliable. */
const GroupDiffRow: React.FC<{
	item: ProductionPublishItem;
	publishing: boolean;
	disabled: boolean;
	onPublish: (item: ProductionPublishItem) => void;
	tokenForType: (contentTypeId?: string | null) => string;
}> = ({ item, publishing, disabled, onPublish, tokenForType }) => {
	const state = useDeploymentDiff(item.contentId, "production");
	const verdict = diffVerdict(state);
	// Les comparaisons intéressantes s'ouvrent seules ; « identique » reste replié.
	const [open, setOpen] = useState<boolean | null>(null);
	const effectiveOpen = open ?? (verdict === "divergent" || verdict === "new" || verdict === "removal");
	const heading = item.title || item.slug || item.contentId || "?";

	return (
		<div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
			<div className="flex flex-wrap items-center gap-2 px-3 py-2">
				<button
					type="button"
					onClick={() => setOpen(!effectiveOpen)}
					className="flex min-w-0 flex-1 items-center gap-2 text-left"
					title={effectiveOpen ? "Replier la comparaison" : "Voir la comparaison"}
				>
					<FiChevronDown className={`h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform ${effectiveOpen ? "rotate-180" : ""}`} />
					<span className={`inline-flex shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${actionChipClass(item.action)}`}>
						{actionLabelFr(item.action)}
					</span>
					<span className="min-w-0 flex-1 truncate text-xs font-semibold text-slate-800" title={heading}>
						{heading}
						<span className="ml-1 font-normal text-slate-400">({item.language})</span>
					</span>
				</button>
				<DiffVerdictChip verdict={verdict} />
				<ItemPublishButton
					item={item}
					failed={false}
					publishing={publishing}
					disabled={disabled}
					onPublish={onPublish}
					tokenForType={tokenForType}
					compact
				/>
			</div>
			{effectiveOpen && (
				<div className="border-t border-slate-100 p-3">
					<DeploymentDiffBody state={state} target="production" compact contentId={item.contentId} />
				</div>
			)}
		</div>
	);
};

/** « Afficher N de plus » : la page suivante des attentes d'un groupe (le serveur en embarque 25). */
const LoadMoreButton: React.FC<{ hiddenCount: number; loading: boolean; onClick: () => void; compact?: boolean }> = ({ hiddenCount, loading, onClick, compact = false }) => {
	const next = Math.min(hiddenCount, GROUP_PAGE_SIZE);
	return (
		<button
			type="button"
			onClick={onClick}
			disabled={loading}
			className={`inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 ${
				compact ? "px-2 py-1 text-[10px]" : "px-3 py-1.5 text-xs"
			}`}
			title={`${hiddenCount} attente${hiddenCount > 1 ? "s" : ""} non affichée${hiddenCount > 1 ? "s" : ""}`}
		>
			{loading ? <LoadingSpinner size="sm" inline /> : <FiChevronDown className="h-3 w-3" />}
			Afficher {next} de plus{hiddenCount > next ? ` (${hiddenCount} restantes)` : ""}
		</button>
	);
};

/** Détail d'un GROUPE : action de groupe + les comparaisons de tous ses éléments, empilées. */
const GroupDetailPanel: React.FC<{
	group: ProductionPublishGroup;
	publishingKey: PublishKey | null;
	onPublishGroup: (group: ProductionPublishGroup) => void;
	onPublishItem: (item: ProductionPublishItem) => void;
	tokenForType: (contentTypeId?: string | null) => string;
	onLoadMore: (contentTypeId: string) => void;
	loadingMore: boolean;
}> = ({ group, publishingKey, onPublishGroup, onPublishItem, tokenForType, onLoadMore, loadingMore }) => {
	const Icon = iconForContentType(group.title, group.contentTypeId);
	const busy = publishingKey !== null;
	const hiddenCount = group.count - group.items.length;

	return (
		<div className="space-y-3">
			<div className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
				<span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-500">
					<Icon className="h-5 w-5" />
				</span>
				<div className="min-w-0 flex-1">
					<h3 className="truncate text-base font-bold text-slate-900">{group.title}</h3>
					<p className="text-xs text-slate-500">
						{pluralFr(group.count, "modification")}
						{group.lastQueuedAtUtc && (
							<span className="text-slate-400" title={exactDateFr(group.lastQueuedAtUtc)}>
								{" "}· dernier ajout {relativeTimeFr(group.lastQueuedAtUtc)}
							</span>
						)}
					</p>
				</div>
				<GatedButton
					token={tokenForType(group.contentTypeId)}
					onClick={() => onPublishGroup(group)}
					disabled={busy}
					className="inline-flex items-center gap-2 rounded-lg border border-[#005CA9]/30 bg-white px-3 py-1.5 text-xs font-semibold text-[#005CA9] shadow-sm transition hover:bg-[#005CA9]/5 disabled:cursor-not-allowed disabled:opacity-50"
				>
					{publishingKey === `type:${group.contentTypeId}` ? <LoadingSpinner size="sm" inline /> : <FiUploadCloud className="h-3.5 w-3.5" />}
					Publier ce groupe ({group.count})
				</GatedButton>
			</div>
			{group.items.map((item) => (
				<GroupDiffRow
					key={itemKey(item)}
					item={item}
					publishing={publishingKey === `item:${item.contentId}`}
					disabled={busy}
					onPublish={onPublishItem}
					tokenForType={tokenForType}
				/>
			))}
			{hiddenCount > 0 && (
				<div className="flex flex-wrap items-center gap-3">
					<LoadMoreButton hiddenCount={hiddenCount} loading={loadingMore} onClick={() => onLoadMore(group.contentTypeId)} />
					<p className="text-xs italic text-slate-400">
						Les {group.items.length} plus récents sont comparés ici ; « Publier ce groupe » couvre l'ensemble ({group.count}).
					</p>
				</div>
			)}
		</div>
	);
};

const Publication: React.FC = () => {
	usePageHeader({ title: "Déploiement" });
	const { overview, loading, error, load, loadMore, loadingMore } = useProductionOverview();
	const { resolveId, resolvePicture } = useUserDirectory();
	const resolvers: AuthorResolvers = { resolveId, resolvePicture };
	const [publishingKey, setPublishingKey] = useState<PublishKey | null>(null);
	const [banner, setBanner] = useState<Banner | null>(null);
	const [lockMessage, setLockMessage] = useState<string | null>(null);
	const [showConfirmAll, setShowConfirmAll] = useState(false);
	const [showConfirmPrune, setShowConfirmPrune] = useState(false);
	const [showConfirmReset, setShowConfirmReset] = useState(false);
	const [resetChecked, setResetChecked] = useState(false);
	const [resetting, setResetting] = useState(false);
	const [showAnalyze, setShowAnalyze] = useState(false);
	const [pruning, setPruning] = useState(false);
	const [pruneProgress, setPruneProgress] = useState<{ checked: number; remaining: number } | null>(null);
	// Trois onglets : Revue (la file de production, pour tous les publiants), Maintenance
	// (l'outillage technique, admins seulement) et Activité (le journal des déploiements).
	const { canFeatureWrite } = usePermissions();
	const canMaintain = canFeatureWrite("deployment");
	// Jeton du bouton publier : le gate serveur applique un OU (clé technique OU jeton périmétré).
	// GatedButton ne vérifie qu'UN jeton, donc on passe celui que l'utilisateur détient réellement :
	// la clé technique pour ses détenteurs (admins, deployer), sinon le promote périmétré du type.
	const tokenForType = useCallback(
		(contentTypeId?: string | null) => (canMaintain ? "feature:deployment:write" : promoteTokenForType(contentTypeId)),
		[canMaintain]
	);
	const [activeTab, setActiveTab] = useState<"review" | "maintenance" | "activity">("review");
	const [selection, setSelection] = useState<Selection>(null);
	// Groupes dépliés dans la liste de gauche (le groupe sélectionné se déplie tout seul).
	const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

	useEffect(() => {
		if (activeTab === "maintenance" && !canMaintain) setActiveTab("review");
	}, [activeTab, canMaintain]);

	useEffect(() => {
		load();
	}, [load]);

	// Another admin's publication (or an auto-publish) finished → silently refresh the queue view.
	useRealtimeEvent("deploy", (payload: any) => {
		if (payload?.target === "production" && payload?.phase === "finish") load(Date.now());
	});

	const publish = useCallback(
		async (key: PublishKey, body: { scope: string; contentTypeId?: string; contentIds?: string[] }) => {
			setPublishingKey(key);
			setBanner(null);
			setLockMessage(null);
			try {
				const res = await globalAxios.post(`/api/deployment/production/publish`, body, {
					withCredentials: true,
					headers: { Accept: "application/json" }
				});
				const d = res.data ?? {};
				const failed = Number(d.failed ?? 0);
				const succeeded = Number(d.succeeded ?? 0);
				setBanner(
					failed > 0
						? { tone: "error", text: `Publication terminée : ${succeeded} réussie${succeeded > 1 ? "s" : ""}, ${pluralFr(failed, "échec")}.` }
						: { tone: "success", text: `Publication réussie : ${pluralFr(succeeded, "élément publié", "éléments publiés")}.` }
				);
			} catch (err: any) {
				console.error(err);
				if (err?.response?.status === 409) {
					setLockMessage(lockMessageFrom(err));
				} else {
					setBanner({ tone: "error", text: "La publication a échoué. Réessayez ou consultez les journaux." });
				}
			} finally {
				setPublishingKey(null);
				load(Date.now());
			}
		},
		[load]
	);

	// « Remettre la revue à zéro » : abandonne TOUTES les attentes (pending et échecs) de la
	// revue de production. Destructif pour les intentions éditoriales : confirmation par case à
	// cocher + en-tête explicite ; à réserver aux remises à plat (un realign ou une republication
	// doit suivre pour ré-aligner la cible).
	const resetQueue = useCallback(async () => {
		setResetting(true);
		setBanner(null);
		try {
			const res = await globalAxios.post(`/api/deployment/production/queue/reset`, null, {
				withCredentials: true,
				headers: { Accept: "application/json", "X-Confirm-Destructive": "I understand" }
			});
			const removed = Number(res.data?.removed ?? 0);
			setBanner({ tone: "success", text: `Revue remise à zéro : ${pluralFr(removed, "attente abandonnée", "attentes abandonnées")}.` });
		} catch (err: any) {
			console.error(err);
			setBanner({ tone: "error", text: err?.response?.data?.message ?? "La remise à zéro a échoué. Réessayez ou consultez les journaux." });
		} finally {
			setResetting(false);
			load(Date.now());
		}
	}, [load]);

	// « Nettoyer les attentes sans changement » : le serveur retire de la file de production les
	// lignes dont l'artefact déployé est déjà identique au brouillon (même comparaison que le
	// diff) ; les échecs, suppressions et contenus non comparables restent.
	const pruneUnchanged = useCallback(async () => {
		setPruning(true);
		setBanner(null);
		try {
			// Le serveur travaille par tranches bornées dans le temps et rend un curseur tant qu'il
			// reste des lignes : on enchaîne les appels et on cumule les compteurs.
			let removed = 0;
			let kept = 0;
			let unsupported = 0;
			let errors = 0;
			let checked = 0;
			let cursor: string | null = null;
			for (let pass = 0; pass < 200; pass++) {
				const res = await globalAxios.post(`/api/deployment/production/prune-unchanged`, null, {
					params: cursor ? { cursor } : undefined,
					withCredentials: true,
					headers: { Accept: "application/json" }
				});
				const d = res.data ?? {};
				removed += Number(d.removed ?? 0);
				kept += Number(d.kept ?? 0);
				unsupported += Number(d.unsupported ?? 0);
				errors += Number(d.errors ?? 0);
				checked += Number(d.checked ?? 0);
				const remaining = Number(d.remaining ?? 0);
				cursor = typeof d.nextCursor === "string" && d.nextCursor ? d.nextCursor : null;
				if (!cursor) break;
				setPruneProgress({ checked, remaining });
			}
			const tail = [
				`${pluralFr(kept, "vraie modification conservée", "vraies modifications conservées")}`,
				unsupported > 0 ? `${pluralFr(unsupported, "non comparable")}` : null,
				errors > 0 ? `${pluralFr(errors, "erreur de comparaison", "erreurs de comparaison")}` : null
			]
				.filter(Boolean)
				.join(", ");
			setBanner({
				tone: errors > 0 ? "error" : "success",
				text:
					removed > 0
						? `Nettoyage terminé : ${pluralFr(removed, "attente sans changement retirée", "attentes sans changement retirées")} ; ${tail}.`
						: `Aucune attente sans changement : ${tail}.`
			});
		} catch (err: any) {
			console.error(err);
			setBanner({ tone: "error", text: "Le nettoyage a échoué. Réessayez ou consultez les journaux." });
		} finally {
			setPruning(false);
			setPruneProgress(null);
			load(Date.now());
		}
	}, [load]);

	const publishAll = () => publish("all", { scope: "all" });
	const publishGroup = (group: ProductionPublishGroup) =>
		publish(`type:${group.contentTypeId}`, { scope: "type", contentTypeId: group.contentTypeId });
	const publishItem = (item: ProductionPublishItem) => {
		if (!item.contentId) return;
		publish(`item:${item.contentId}`, { scope: "items", contentIds: [item.contentId] });
	};
	const retryAllFailed = () => {
		const ids = overview.failed.map((i) => i.contentId).filter((id): id is string => !!id);
		if (ids.length === 0) return;
		publish("retry-all", { scope: "items", contentIds: ids });
	};

	const { pendingTotal, failedTotal, failed, groups } = overview;
	const busy = publishingKey !== null || pruning;

	// La sélection survit aux rafraîchissements tant que l'objet existe encore dans la file.
	const selectedGroup =
		selection?.kind === "group" ? groups.find((g) => g.contentTypeId === selection.typeId) ?? null : null;
	const allItems: Array<{ item: ProductionPublishItem; failed: boolean }> = [
		...failed.map((i) => ({ item: i, failed: true })),
		...groups.reduce<Array<{ item: ProductionPublishItem; failed: boolean }>>(
			(acc, g) => acc.concat(g.items.map((i) => ({ item: i, failed: false }))),
			[]
		)
	];
	const selectedEntry =
		selection?.kind === "item" ? allItems.find((e) => itemKey(e.item) === selection.queueId) ?? null : null;

	const selectGroup = (group: ProductionPublishGroup) => {
		setSelection({ kind: "group", typeId: group.contentTypeId });
		setExpandedGroups((prev) => ({ ...prev, [group.contentTypeId]: true }));
	};
	const selectItem = (item: ProductionPublishItem) => setSelection({ kind: "item", queueId: itemKey(item) });

	const heroState: "failed" | "pending" | "ok" = failedTotal > 0 ? "failed" : pendingTotal > 0 ? "pending" : "ok";
	const hero =
		heroState === "failed"
			? {
					ring: "border-rose-200",
					iconWrap: "bg-rose-100 text-rose-600",
					icon: <FiAlertTriangle className="h-6 w-6" />,
					title: `${pluralFr(failedTotal, "échec")} de publication`,
					titleClass: "text-rose-700",
					sub: "Corrigez ou réessayez les éléments en échec avant de publier le reste."
			  }
			: heroState === "pending"
			? {
					ring: "border-amber-200",
					iconWrap: "bg-amber-100 text-amber-600",
					icon: <FiClock className="h-6 w-6" />,
					title: `${pluralFr(pendingTotal, "modification")} à publier`,
					titleClass: "text-slate-900",
					sub: "Sélectionnez un élément ou un groupe à gauche pour revoir ses différences avant de publier."
			  }
			: {
					ring: "border-emerald-200",
					iconWrap: "bg-emerald-100 text-emerald-600",
					icon: <FiCheckCircle className="h-6 w-6" />,
					title: "Production à jour ✓",
					titleClass: "text-emerald-700",
					sub: "Tout le contenu publié est en ligne. Les contenus à publication automatique (cours de change, gouverneurs…) se mettent à jour sans action de votre part."
			  };

	const tabButtonClass = (tab: "review" | "maintenance" | "activity") =>
		`rounded-full px-4 py-1.5 text-sm font-semibold transition ${
			activeTab === tab ? "bg-slate-900 text-white shadow-md" : "text-slate-600 hover:bg-slate-100"
		}`;

	return (
		<div className="w-full space-y-5 p-4 sm:p-6">
			{/* Top bar: preview status (discreet) + connections */}
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div className="flex flex-wrap items-center gap-3">
					<p className="text-xs uppercase tracking-[0.3em] text-slate-400">Production</p>
					<div className="flex items-center gap-1 rounded-full border border-slate-200 bg-white p-1 shadow-sm">
						<button type="button" onClick={() => setActiveTab("review")} className={tabButtonClass("review")}>
							Revue
							{pendingTotal + failedTotal > 0 && (
								<span className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${failedTotal > 0 ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>
									{pendingTotal + failedTotal}
								</span>
							)}
						</button>
						{canMaintain && (
							<button type="button" onClick={() => setActiveTab("maintenance")} className={tabButtonClass("maintenance")}>
								Maintenance
							</button>
						)}
						<button type="button" onClick={() => setActiveTab("activity")} className={tabButtonClass("activity")}>
							Activité
						</button>
					</div>
				</div>
				<div className="flex items-center gap-3">
					<PreviewPill />
					<ConnectionsModalButton className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50" />
				</div>
			</div>

			{activeTab === "review" && (
			<>
			{error && (
				<div className="flex items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-700">
					{error}
					<button type="button" onClick={() => load(Date.now())} className="inline-flex items-center gap-1 text-xs font-semibold hover:underline">
						<FiRefreshCw /> Réessayer
					</button>
				</div>
			)}
			{lockMessage && (
				<div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm font-medium text-amber-800" role="status">
					<FiClock className="shrink-0" /> {lockMessage}
				</div>
			)}
			{banner && (
				<div
					className={`flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm ${
						banner.tone === "success"
							? "border-emerald-200 bg-emerald-50 text-emerald-700"
							: "border-rose-200 bg-rose-50 text-rose-700"
					}`}
					role="status"
				>
					{banner.tone === "success" ? <FiCheckCircle className="shrink-0" /> : <FiAlertTriangle className="shrink-0" />}
					{banner.text}
				</div>
			)}

			{/* Hero: the production state, at a glance */}
			<section className={`rounded-2xl border bg-white px-6 py-4 shadow-sm ${hero.ring}`}>
				{loading ? (
					<div className="flex items-center gap-3 py-4 text-sm text-slate-400">
						<LoadingSpinner size="sm" /> Chargement de l'état de la production…
					</div>
				) : (
					<div className="flex flex-wrap items-center justify-between gap-4">
						<div className="flex items-center gap-4">
							<span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${hero.iconWrap}`}>{hero.icon}</span>
							<div>
								<h2 className={`text-lg font-bold ${hero.titleClass}`}>{hero.title}</h2>
								<p className="mt-0.5 text-sm text-slate-500">{hero.sub}</p>
							</div>
						</div>
						<div className="flex flex-wrap items-center gap-2">
							<button
								type="button"
								onClick={() => setShowAnalyze(true)}
								className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
								title="Comparer les fichiers deja publies (preview ou production) a ce que la source produirait, puis corriger les ecarts"
							>
								<FiFileText className="h-4 w-4" /> Analyser les fichiers déployés
							</button>
							<GatedButton
								token="feature:deployment:write"
								onClick={() => setShowConfirmPrune(true)}
								disabled={busy || pendingTotal === 0}
								className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
								title="Retirer de la file les attentes dont la publication ne changerait rien en production"
							>
								{pruning ? <LoadingSpinner size="sm" inline /> : <FiFilter className="h-4 w-4" />}
								{pruning
									? pruneProgress
										? `Nettoyage… ${pruneProgress.checked} vérifiée${pruneProgress.checked > 1 ? "s" : ""}, ${pruneProgress.remaining} restante${pruneProgress.remaining > 1 ? "s" : ""}`
										: "Nettoyage…"
									: "Nettoyer les attentes sans changement"}
							</GatedButton>
							<GatedButton
								token="feature:deployment:write"
								onClick={() => {
									setResetChecked(false);
									setShowConfirmReset(true);
								}}
								disabled={busy || resetting || pendingTotal === 0}
								className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-white px-4 py-2 text-sm font-semibold text-rose-700 shadow-sm transition hover:border-rose-300 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
								title="Abandonner toutes les attentes de la revue (les intentions éditoriales en cours sont perdues) ; à faire suivre d'un réalignement"
							>
								{resetting ? <LoadingSpinner size="sm" inline /> : <FiTrash2 className="h-4 w-4" />}
								{resetting ? "Remise à zéro…" : "Remettre la revue à zéro"}
							</GatedButton>
							<GatedButton
								token="feature:deployment:write"
								onClick={() => setShowConfirmAll(true)}
								disabled={busy || pendingTotal === 0}
								className={PRIMARY_BTN}
							>
								{publishingKey === "all" ? <LoadingSpinner size="sm" inline /> : <FiUploadCloud className="h-4 w-4" />}
								{publishingKey === "all" ? "Publication…" : `Publier tout en production (${pendingTotal})`}
							</GatedButton>
						</div>
					</div>
				)}
			</section>

			{/* Maître-détail : la file à gauche, la sélection et ses différences à droite.
			    Grille inline : les variantes responsives Tailwind sont neutralisées par le CSS du SDK
			    (un .grid-cols-1 plein arrive plus tard dans la cascade) ; le style inline est immunisé. */}
			{(groups.length > 0 || failedTotal > 0) && (
				<div style={{ display: "grid", gridTemplateColumns: "minmax(300px, 360px) minmax(0, 1fr)", gap: "1.25rem", alignItems: "start" }}>
					<aside className="min-w-0 space-y-3">
						{failedTotal > 0 && (
							<div className="overflow-hidden rounded-2xl border border-rose-200 bg-white shadow-sm">
								<div className="flex flex-wrap items-center justify-between gap-2 border-b border-rose-100 bg-rose-50/70 px-4 py-2.5">
									<h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-rose-700">
										<FiAlertTriangle /> Échecs de publication ({failedTotal})
									</h3>
									<GatedButton
										token="feature:deployment:write"
										onClick={retryAllFailed}
										disabled={busy || failed.every((i) => !i.contentId)}
										className="inline-flex items-center gap-1.5 rounded-lg border border-rose-300 bg-white px-2 py-1 text-[11px] font-semibold text-rose-700 shadow-sm transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
									>
										{publishingKey === "retry-all" ? <LoadingSpinner size="sm" inline /> : <FiRefreshCw className="h-3 w-3" />}
										Tout réessayer
									</GatedButton>
								</div>
								<div className="space-y-1 p-2">
									{failed.map((item) => (
										<ItemRow
											key={itemKey(item)}
											item={item}
											failed
											selected={selection?.kind === "item" && selection.queueId === itemKey(item)}
											onSelect={() => selectItem(item)}
										/>
									))}
								</div>
							</div>
						)}

						{groups.length > 0 && (
							<div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
								<div className="border-b border-slate-100 px-4 py-2.5">
									<h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">À publier, par type de contenu</h3>
								</div>
								<div className="space-y-1 p-2">
									{groups.map((group) => {
										const Icon = iconForContentType(group.title, group.contentTypeId);
										const expanded = expandedGroups[group.contentTypeId] ?? false;
										const groupSelected = selection?.kind === "group" && selection.typeId === group.contentTypeId;
										const hiddenCount = group.count - group.items.length;
										return (
											<div key={group.contentTypeId}>
												<div
													className={`flex items-center gap-1 rounded-lg border transition ${
														groupSelected ? "border-[#005CA9]/50 bg-[#005CA9]/5" : "border-transparent hover:border-slate-200 hover:bg-slate-50"
													}`}
												>
													<button
														type="button"
														onClick={() => selectGroup(group)}
														className="flex min-w-0 flex-1 items-center gap-2.5 px-2 py-2 text-left"
														title={`Comparer tout le groupe ${group.title}`}
													>
														<span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
															<Icon className="h-3.5 w-3.5" />
														</span>
														<span className="min-w-0 flex-1">
															<span className="block truncate text-xs font-semibold text-slate-800">{group.title}</span>
															<span className="block text-[10px] text-slate-400">{pluralFr(group.count, "modification")}</span>
														</span>
													</button>
													<button
														type="button"
														onClick={() =>
															setExpandedGroups((prev) => ({ ...prev, [group.contentTypeId]: !expanded }))
														}
														title={expanded ? "Replier" : "Voir les éléments"}
														className="mr-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
													>
														<FiChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
													</button>
												</div>
												{expanded && (
													<div className="ml-4 mt-1 space-y-1 border-l border-slate-100 pl-2">
														{group.items.map((item) => (
															<ItemRow
																key={itemKey(item)}
																item={item}
																selected={selection?.kind === "item" && selection.queueId === itemKey(item)}
																onSelect={() => selectItem(item)}
															/>
														))}
														{hiddenCount > 0 && (
															<div className="px-2 py-1">
																<LoadMoreButton
																	compact
																	hiddenCount={hiddenCount}
																	loading={loadingMore[group.contentTypeId] === true}
																	onClick={() => loadMore(group.contentTypeId)}
																/>
															</div>
														)}
													</div>
												)}
											</div>
										);
									})}
								</div>
							</div>
						)}
						<p className="px-1 text-[11px] text-slate-400">
							Les contenus à publication automatique sont mis en ligne dès leur synchronisation et n'apparaissent pas ici.
							Une ligne « supprimé » attend le retrait du contenu de la production.
						</p>
					</aside>

					<section className="min-w-0">
						{selectedEntry ? (
							<ItemDetailPanel
								key={itemKey(selectedEntry.item)}
								item={selectedEntry.item}
								failed={selectedEntry.failed}
								publishing={publishingKey === `item:${selectedEntry.item.contentId}`}
								disabled={busy}
								onPublish={publishItem}
								tokenForType={tokenForType}
								resolvers={resolvers}
							/>
						) : selectedGroup ? (
							<GroupDetailPanel
								key={selectedGroup.contentTypeId}
								group={selectedGroup}
								publishingKey={publishingKey}
								onPublishGroup={publishGroup}
								onPublishItem={publishItem}
								tokenForType={tokenForType}
								onLoadMore={loadMore}
								loadingMore={loadingMore[selectedGroup.contentTypeId] === true}
							/>
						) : (
							<div className="flex min-h-[220px] items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white/60 px-6 py-12 text-center">
								<div className="max-w-sm space-y-2">
									<FiColumns className="mx-auto h-6 w-6 text-slate-300" aria-hidden />
									<p className="text-sm font-medium text-slate-600">Sélectionnez un élément ou un groupe</p>
									<p className="text-xs text-slate-400">
										Les différences avec la production s'affichent ici : par blocs colorés ou en texte unifié,
										comme dans l'Audit. Un groupe empile les comparaisons de tous ses éléments.
									</p>
								</div>
							</div>
						)}
					</section>
				</div>
			)}
			</>
			)}

			{/* Onglet Maintenance : l'outillage technique (file brute, analyse, realign, resync,
			    promotion héritée), réservé aux détenteurs de la clé technique. Le hard-clean et
			    les autres opérations lourdes gardent leur maison canonique dans Réglages. */}
			{activeTab === "maintenance" && canMaintain && (
				<div className="space-y-5">
					<AdvancedDeploymentTools />
					<div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
						<h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-700">
							<FiSettings /> Opérations lourdes
						</h3>
						<p className="mt-1 text-sm text-slate-500">
							Le hard-clean + republication, le contrôle des fichiers déployés et la republication forcée
							de la preview restent dans les Réglages, avec leurs garde-fous.
						</p>
						<Link
							to="/settings"
							className="mt-3 inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
						>
							Ouvrir Réglages, onglet Déploiement
						</Link>
					</div>
				</div>
			)}

			{/* Onglet Activité : le journal des déploiements preview/production, paginé */}
			{activeTab === "activity" && <DeploymentActivity />}

			{/* Analyse des fichiers deployes (revue de l'existant, correction par cible) */}
			<IntegrityAnalyzeModal show={showAnalyze} onHide={() => setShowAnalyze(false)} />

			{/* Prune-unchanged confirmation */}
			<Modal show={showConfirmPrune} onHide={() => setShowConfirmPrune(false)} size="md" centered>
				<Modal.Header closeButton>
					<Modal.Title>Nettoyer les attentes sans changement ?</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p className="text-sm text-slate-700">
						Chaque attente ({pendingTotal}) est comparée à ce qui est déjà en production ; celles dont la
						publication ne changerait rien sont retirées de la file. Les vraies modifications, les échecs,
						les suppressions et les contenus non comparables restent.
					</p>
					<p className="mt-2 text-xs text-slate-500">Rien n'est publié : la production n'est pas touchée.</p>
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						onClick={() => setShowConfirmPrune(false)}
						className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
					>
						Annuler
					</button>
					<button
						type="button"
						onClick={() => {
							setShowConfirmPrune(false);
							pruneUnchanged();
						}}
						className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
					>
						<FiFilter className="h-4 w-4" /> Nettoyer la file
					</button>
				</Modal.Footer>
			</Modal>

			{/* Queue-reset confirmation */}
			<Modal show={showConfirmReset} onHide={() => setShowConfirmReset(false)} size="md" centered>
				<Modal.Header closeButton>
					<Modal.Title>Remettre la revue à zéro ?</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p className="text-sm text-slate-700">
						{pluralFr(pendingTotal, "attente sera abandonnée", "attentes seront abandonnées")} sans être
						{pendingTotal > 1 ? "s" : ""} publiée{pendingTotal > 1 ? "s" : ""} : les modifications, suppressions et
						échecs en cours quittent la revue. La production n'est pas touchée.
					</p>
					<p className="mt-2 text-xs text-rose-700">
						À réserver aux remises à plat : faites suivre d'un réalignement (« Analyser les fichiers déployés »)
						pour que la production reste fidèle à la source.
					</p>
					<label className="mt-3 flex items-start gap-2 text-sm text-slate-700">
						<input
							type="checkbox"
							checked={resetChecked}
							onChange={(e) => setResetChecked(e.target.checked)}
							className="mt-0.5"
						/>
						<span>Je comprends que les attentes éditoriales en cours seront perdues.</span>
					</label>
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						onClick={() => setShowConfirmReset(false)}
						className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
					>
						Annuler
					</button>
					<button
						type="button"
						disabled={!resetChecked}
						onClick={() => {
							setShowConfirmReset(false);
							resetQueue();
						}}
						className="inline-flex items-center gap-2 rounded-full bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-500 disabled:cursor-not-allowed disabled:opacity-50"
					>
						<FiTrash2 className="h-4 w-4" /> Confirmer la remise à zéro
					</button>
				</Modal.Footer>
			</Modal>

			{/* Publish-all confirmation */}
			<Modal show={showConfirmAll} onHide={() => setShowConfirmAll(false)} size="md" centered>
				<Modal.Header closeButton>
					<Modal.Title>Publier tout en production ?</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p className="text-sm text-slate-700">
						{pluralFr(pendingTotal, "modification sera publiée", "modifications seront publiées")} et
						immédiatement visible{pendingTotal > 1 ? "s" : ""} par les visiteurs du site.
					</p>
					{groups.length > 0 && (
						<ul className="mt-3 space-y-1.5 rounded-xl border border-slate-200 bg-slate-50 p-3">
							{groups.map((group) => (
								<li key={`confirm-${group.contentTypeId}`} className="flex items-center justify-between gap-2 text-sm">
									<span className="flex items-center gap-2 truncate text-slate-700">
										{group.title}
									</span>
									<span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-slate-600 shadow-sm">
										{group.count}
									</span>
								</li>
							))}
						</ul>
					)}
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						onClick={() => setShowConfirmAll(false)}
						className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
					>
						Annuler
					</button>
					<button
						type="button"
						onClick={() => {
							setShowConfirmAll(false);
							publishAll();
						}}
						className="inline-flex items-center gap-2 rounded-full bg-[#005CA9] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#004b8a]"
					>
						<FiUploadCloud className="h-4 w-4" /> Confirmer la publication
					</button>
				</Modal.Footer>
			</Modal>
		</div>
	);
};

export default Publication;
